import React, { useState } from 'react';
import { Box, Typography, Container, CircularProgress, Alert, Fade } from '@mui/material';
import { useDashboard } from '../hooks/useDashboard';
import SalesFilter from '../components/salesAnalytics/SalesFilter';
import SalesOverview from '../components/salesAnalytics/SalesOverview';
import SalesTrendChart from '../components/salesAnalytics/SalesTrendChart';
import SalesCategoryChart from '../components/salesAnalytics/SalesCategoryChart';
import SalesTopProducts from '../components/salesAnalytics/SalesTopProducts';
import SalesByWarehouse from '../components/salesAnalytics/SalesByWarehouse';
import SalesOrderStatus from '../components/salesAnalytics/SalesOrderStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie } from '@fortawesome/free-solid-svg-icons';
import { useWarehouseContext } from '../context/WarehouseContext';
import { useTranslation } from 'react-i18next';

const SalesAnalytics = () => {
    const { t } = useTranslation();
    const {
        getSalesOverview,
        getSalesTrend,
        getSalesByCategory,
        getSalesTopProducts,
        getSalesByWarehouse,
        getSalesOrderStatus
    } = useDashboard();

    const [period, setPeriod] = useState('last_30_days');

    const [dateRange, setDateRange] = useState(() => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
            from_date: startOfMonth.toISOString().split('T')[0],
            to_date: endOfMonth.toISOString().split('T')[0],
        };
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedWarehouseIds } = useWarehouseContext();

    // State for all data sections
    const [data, setData] = useState({
        overview: null,
        trend: null,
        category: null,
        topProducts: null,
        warehouse: null,
        orderStatus: null
    });

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({ ...prev, [field]: value }));
    };

    const handlePeriodChange = (value) => {
        setPeriod(value);
    };

    // Ref to track if a fetch is in progress to prevent double calls (Strict Mode)
    const fetchingRef = React.useRef(false);

    const fetchData = React.useCallback(async (force = false) => {
        // For custom period, require dates
        if (period === 'custom' && (!dateRange.from_date || !dateRange.to_date)) return;

        // Prevent double fetch if already fetching
        if (fetchingRef.current) return;

        const warehouseKey = selectedWarehouseIds?.length > 0 ? `_w${selectedWarehouseIds.join('_')}` : '';
        const cacheKey = period === 'custom'
            ? `salesAnalytics_custom_${dateRange.from_date}_${dateRange.to_date}${warehouseKey}`
            : `salesAnalytics_${period}${warehouseKey}`;
        const cachedData = localStorage.getItem(cacheKey);

        if (!force && cachedData) {
            try {
                const parsed = JSON.parse(cachedData);
                if (parsed) {
                    setData(parsed);
                    return;
                }
            } catch (e) {
                console.error("Failed to parse cached data", e);
                localStorage.removeItem(cacheKey);
            }
        }

        fetchingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            const params = { period };

            // Only include dates for custom period
            if (period === 'custom') {
                params.from_date = dateRange.from_date;
                params.to_date = dateRange.to_date;
            }

            // Execute all requests in parallel
            const [
                overviewRes,
                trendRes,
                categoryRes,
                productsRes,
                warehouseRes,
                statusRes
            ] = await Promise.all([
                getSalesOverview(params),
                getSalesTrend(params),
                getSalesByCategory(params),
                getSalesTopProducts(params),
                getSalesByWarehouse(params),
                getSalesOrderStatus(params)
            ]);

            // Check for validation/errors in responses
            const newError = overviewRes.error || trendRes.error || categoryRes.error ||
                productsRes.error || warehouseRes.error || statusRes.error;

            if (newError) {
                setError(newError);
            }

            const newData = {
                overview: overviewRes.data || null,
                trend: trendRes.data || null,
                category: categoryRes.data || null,
                topProducts: productsRes.data || null,
                warehouse: warehouseRes.data || null,
                orderStatus: statusRes.data || null
            };

            setData(newData);
            localStorage.setItem(cacheKey, JSON.stringify(newData));

        } catch (err) {
            setError(err.message || t('hookErrors.analyticsPage.fetchFailed'));
            console.error(err);
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [period, dateRange, getSalesOverview, getSalesTrend, getSalesByCategory, getSalesTopProducts, getSalesByWarehouse, getSalesOrderStatus, selectedWarehouseIds]);

    // Initial fetch on mount
    React.useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWarehouseIds]); // Run once on mount or when warehouse selection changes

    const strings = t('salesAnalytics.page', { returnObjects: true });

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            <Box mb={5}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box
                        sx={{
                            bgcolor: 'primary.main',
                            borderRadius: 2,
                            width: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                        }}
                    >
                        <FontAwesomeIcon icon={faChartPie} />
                    </Box>
                    <Typography variant="h4" fontWeight="800" letterSpacing="-0.5px">
                        {strings.title}
                    </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800 }}>
                    {strings.subtitle}
                </Typography>
            </Box>

            <SalesFilter
                fromDate={dateRange.from_date}
                toDate={dateRange.to_date}
                onFromDateChange={(val) => handleDateChange('from_date', val)}
                onToDateChange={(val) => handleDateChange('to_date', val)}
                onSubmit={() => fetchData(true)}
                loading={loading}
                period={period}
                onPeriodChange={handlePeriodChange}
            />

            {error && (
                <Fade in>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {typeof error === 'object' ? JSON.stringify(error) : error}
                    </Alert>
                </Fade>
            )}

            {loading && !data.overview && (
                <Box display="flex" justifyContent="center" p={10}>
                    <CircularProgress size={40} thickness={4} />
                </Box>
            )}

            {/* Data Components - Using Fade for smooth entry */}
            {data.overview && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                    {/* Overview KPIs */}
                    <SalesOverview data={data.overview} />

                    {/* Trend + Category */}
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        <div style={{ flex: '2 1 420px', minWidth: 320 }}>
                            {data.trend && <SalesTrendChart data={data.trend} />}
                        </div>
                        <div style={{ flex: '1 1 280px', minWidth: 260 }}>
                            {data.category && <SalesCategoryChart data={data.category} />}
                        </div>
                    </div>

                    {/* Order Status - full width */}
                    {data.orderStatus && <SalesOrderStatus data={data.orderStatus} />}

                    {/* Top Products + Warehouse */}
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 380px', minWidth: 320 }}>
                            {data.topProducts && <SalesTopProducts data={data.topProducts} />}
                        </div>
                        <div style={{ flex: '1 1 300px', minWidth: 260 }}>
                            {data.warehouse && <SalesByWarehouse data={data.warehouse} />}
                        </div>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default SalesAnalytics;
